/* eslint-disable no-param-reassign */
import { createAsyncThunk } from '@reduxjs/toolkit';

import localization from '../../shared/strings';
import { selectProductSelectionPayload } from '../selectors/productSelectionSelectors';
import { fetchAggregatedProductDetails } from './fetchAggregatedProductDetails';
import {
  applyBulkDraftFieldPatchToEntities,
  applyBulkMasterFieldPatchToItems,
} from './helpers/applyBulkMasterFieldPatch';
import applySelectionCommitToState from './helpers/applySelectionCommitToState';
import {
  buildBulkMasterUpdateData,
  getAggregatedSavingKey,
  mergeBulkUpdateResults,
  resolveBulkMasterTargets,
  splitListingSelectionItems,
  splitMasterSelectionItems,
} from './helpers/buildBulkMasterFieldUpdate';
import {
  buildIdentityMasterUpdateData,
  isIdentityMasterFieldKey,
  pickIdentityListingWriteTarget,
  pickIdentityMasterWriteTarget,
} from './helpers/buildIdentityMasterFieldUpdate';
import { formatProductUpdateErrorMessage } from './helpers/formatProductUpdateErrorMessage';
import { isDraftSelectionContext, isMasterSelectionContext } from './helpers/selectionContext';
import { buildSelectionKey } from './helpers/selectionKey';

const aggregatedL10n = localization.PRODUCT_INFO.aggregated;

const IDENTITY_SUCCESS_SUMMARY = {
  total: 1,
  updated: 1,
  skipped: [],
  failed: [],
};

const scheduleSilentRefetch = ({
  getState, dispatch, requestId, savingKey,
}) => {
  Promise.resolve()
    .then(() => Promise.resolve())
    .then(() => Promise.resolve())
    .then(() => {
      const lastAppliedId = getState().products.ui.aggregatedDetails
        .lastAppliedByKey?.[savingKey];
      if (lastAppliedId !== requestId) return;

      dispatch(fetchAggregatedProductDetails({ silent: true })).catch(() => {});
    })
    .catch(() => {});
};

const toastAndLogAggregatedMutationError = ({
  error, Logger, Toast, utils, fieldKey,
}) => {
  const rawMessage = utils.getDataFromResponceError(error, 'msg');
  const errorMessage = rawMessage
    ? formatProductUpdateErrorMessage(rawMessage)
    : aggregatedL10n.updateFailed;
  Toast(errorMessage, { type: 'error', autoClose: 5000 });
  Logger.error(new Error('Error bulk updating aggregated master field'), { error }, [
    'UpdateAggregatedFieldFailed',
    {
      fieldKey,
      errorMessage: rawMessage || errorMessage || 'NoMessage',
    },
  ]);
};

const runIdentityMasterFieldMutation = async ({
  fieldKey,
  value,
  target,
  isClear,
}, {
  getState,
  dispatch,
  requestId,
  extra: {
    sdk, Logger, Toast, utils,
  },
}) => {
  const data = buildIdentityMasterUpdateData(fieldKey, value, { isClear });
  if (!data) {
    Toast(aggregatedL10n.updateFailed, { type: 'error', autoClose: 5000 });
    return { ignored: true, reason: 'unsupportedField' };
  }

  const writeTarget = pickIdentityMasterWriteTarget(
    getState().products.ui.selection,
    fieldKey,
    { target },
  );
  if (!writeTarget) {
    Toast(aggregatedL10n.updateFailed, { type: 'error', autoClose: 5000 });
    return { ignored: true, reason: 'noApplicableIds' };
  }

  const savingKey = getAggregatedSavingKey({ fieldKey, target, isMeta: false });
  const productIds = writeTarget.kind === 'product' ? [writeTarget.productId] : [];
  const variantIds = writeTarget.kind === 'variant' ? [writeTarget.variantId] : [];

  try {
    if (writeTarget.kind === 'variant') {
      await sdk.products.updateVariant(
        writeTarget.productId,
        writeTarget.variantId,
        data,
      );
    } else {
      await sdk.products.updateMasterProduct(writeTarget.productId, data);
    }

    scheduleSilentRefetch({
      getState, dispatch, requestId, savingKey,
    });

    return {
      ignored: false,
      fieldKey,
      target,
      isMeta: false,
      summary: IDENTITY_SUCCESS_SUMMARY,
      data,
      productIds,
      variantIds,
    };
  } catch (error) {
    toastAndLogAggregatedMutationError({
      error, Logger, Toast, utils, fieldKey,
    });
    throw error;
  }
};

const runIdentityDraftFieldMutation = async ({
  fieldKey,
  value,
  target,
  isClear,
}, {
  getState,
  dispatch,
  requestId,
  extra: {
    sdk, Logger, Toast, utils,
  },
}) => {
  const data = buildIdentityMasterUpdateData(fieldKey, value, { isClear });
  if (!data) {
    Toast(aggregatedL10n.updateFailed, { type: 'error', autoClose: 5000 });
    return { ignored: true, reason: 'unsupportedField' };
  }

  const writeTarget = pickIdentityListingWriteTarget(
    getState().products.ui.selection,
    fieldKey,
    { target },
  );
  if (!writeTarget) {
    Toast(aggregatedL10n.updateFailed, { type: 'error', autoClose: 5000 });
    return { ignored: true, reason: 'noApplicableIds' };
  }

  const savingKey = getAggregatedSavingKey({ fieldKey, target, isMeta: false });
  const listingIds = writeTarget.kind === 'product' ? [writeTarget.productId] : [];
  const variantIds = writeTarget.kind === 'variant' ? [writeTarget.variantId] : [];

  try {
    if (writeTarget.kind === 'variant') {
      await sdk.products.updateVariant(
        writeTarget.productId,
        writeTarget.variantId,
        data,
      );
    } else {
      await sdk.products.updateListing(writeTarget.productId, data);
    }

    scheduleSilentRefetch({
      getState, dispatch, requestId, savingKey,
    });

    return {
      ignored: false,
      fieldKey,
      target,
      isMeta: false,
      summary: IDENTITY_SUCCESS_SUMMARY,
      data,
      listingIds,
      variantIds,
    };
  } catch (error) {
    toastAndLogAggregatedMutationError({
      error, Logger, Toast, utils, fieldKey,
    });
    throw error;
  }
};

const runBulkMasterFieldMutation = async ({
  fieldKey,
  value,
  target,
  isMeta,
  isClear,
}, {
  getState,
  dispatch,
  requestId,
  extra: {
    sdk, Logger, Toast, utils,
  },
}) => {
  const state = getState();

  if (isIdentityMasterFieldKey(fieldKey, { isMeta })) {
    return runIdentityMasterFieldMutation({
      fieldKey,
      value,
      target,
      isClear,
    }, {
      getState,
      dispatch,
      requestId,
      extra: {
        sdk, Logger, Toast, utils,
      },
    });
  }

  const data = buildBulkMasterUpdateData(fieldKey, value, { isMeta, isClear });
  if (!data) {
    Toast(aggregatedL10n.updateFailed, { type: 'error', autoClose: 5000 });
    return { ignored: true, reason: 'unsupportedField' };
  }

  const items = selectProductSelectionPayload(state);
  const targets = resolveBulkMasterTargets(fieldKey, { isMeta, target });
  const { productIds, variantIds } = splitMasterSelectionItems(items);
  const requestedProductIds = targets.includes('product') && productIds.length
    ? productIds
    : [];
  const requestedVariantIds = targets.includes('variant') && variantIds.length
    ? variantIds
    : [];

  const requests = [];
  if (requestedProductIds.length) {
    requests.push({
      kind: 'product',
      promise: sdk.products.updateMasterProducts({
        productIds: requestedProductIds,
        data,
      }),
    });
  }
  if (requestedVariantIds.length) {
    requests.push({
      kind: 'variant',
      promise: sdk.products.updateMasterVariants({
        variantIds: requestedVariantIds,
        data,
      }),
    });
  }

  if (!requests.length) {
    Toast(aggregatedL10n.updateFailed, { type: 'error', autoClose: 5000 });
    return { ignored: true, reason: 'noApplicableIds' };
  }

  try {
    const results = await Promise.all(requests.map(async (request) => {
      const { data: body } = await request.promise;
      return { kind: request.kind, ...body };
    }));

    const summary = mergeBulkUpdateResults(results);
    const savingKey = getAggregatedSavingKey({ fieldKey, target, isMeta });

    if (summary.updated === 0 && !summary.failed.length && summary.skipped.length) {
      Toast(aggregatedL10n.recordsUnavailable(summary.skipped.length), {
        type: 'warning',
        autoClose: 5000,
      });
    } else if (summary.failed.length || summary.skipped.length) {
      Toast(aggregatedL10n.partialUpdate(summary.updated, summary.total), {
        type: 'warning',
        autoClose: 5000,
      });
    }

    scheduleSilentRefetch({
      getState, dispatch, requestId, savingKey,
    });

    return {
      ignored: false,
      fieldKey,
      target,
      isMeta,
      summary,
      data,
      productIds: requestedProductIds,
      variantIds: requestedVariantIds,
    };
  } catch (error) {
    toastAndLogAggregatedMutationError({
      error, Logger, Toast, utils, fieldKey,
    });
    throw error;
  }
};

const runBulkDraftFieldMutation = async ({
  fieldKey,
  value,
  target,
  isMeta,
  isClear,
}, {
  getState,
  dispatch,
  requestId,
  extra: {
    sdk, Logger, Toast, utils,
  },
}) => {
  const state = getState();
  const { context } = state.products.ui.selection;
  const { channelId } = context;

  if (isIdentityMasterFieldKey(fieldKey, { isMeta })) {
    return runIdentityDraftFieldMutation({
      fieldKey,
      value,
      target,
      isClear,
    }, {
      getState,
      dispatch,
      requestId,
      extra: {
        sdk, Logger, Toast, utils,
      },
    });
  }

  const data = buildBulkMasterUpdateData(fieldKey, value, { isMeta, isClear });
  if (!data) {
    Toast(aggregatedL10n.updateFailed, { type: 'error', autoClose: 5000 });
    return { ignored: true, reason: 'unsupportedField' };
  }

  const items = selectProductSelectionPayload(state);
  const targets = resolveBulkMasterTargets(fieldKey, { isMeta, target });
  const { listingIds, variantIds } = splitListingSelectionItems(items);
  const requestedListingIds = targets.includes('product') && listingIds.length
    ? listingIds
    : [];
  const requestedVariantIds = targets.includes('variant') && variantIds.length
    ? variantIds
    : [];

  const requests = [];
  if (requestedListingIds.length) {
    requests.push({
      kind: 'product',
      layer: 'listing',
      promise: sdk.products.updateDraftListings({
        channelId,
        listingIds: requestedListingIds,
        data,
      }),
    });
  }
  if (requestedVariantIds.length) {
    requests.push({
      kind: 'variant',
      layer: 'listing',
      promise: sdk.products.updateDraftListingVariants({
        channelId,
        variantIds: requestedVariantIds,
        data,
      }),
    });
  }

  if (!requests.length) {
    Toast(aggregatedL10n.updateFailed, { type: 'error', autoClose: 5000 });
    return { ignored: true, reason: 'noApplicableIds' };
  }

  try {
    const results = await Promise.all(requests.map(async (request) => {
      const { data: body } = await request.promise;
      return { kind: request.kind, layer: request.layer, ...body };
    }));

    const summary = mergeBulkUpdateResults(results);
    const savingKey = getAggregatedSavingKey({ fieldKey, target, isMeta });

    if (summary.updated === 0 && !summary.failed.length && summary.skipped.length) {
      Toast(aggregatedL10n.recordsUnavailable(summary.skipped.length), {
        type: 'warning',
        autoClose: 5000,
      });
    } else if (summary.failed.length || summary.skipped.length) {
      Toast(aggregatedL10n.partialUpdate(summary.updated, summary.total), {
        type: 'warning',
        autoClose: 5000,
      });
    }

    scheduleSilentRefetch({
      getState, dispatch, requestId, savingKey,
    });

    return {
      ignored: false,
      fieldKey,
      target,
      isMeta,
      summary,
      data,
      listingIds: requestedListingIds,
      variantIds: requestedVariantIds,
    };
  } catch (error) {
    toastAndLogAggregatedMutationError({
      error, Logger, Toast, utils, fieldKey,
    });
    throw error;
  }
};

const runAggregatedFieldMutation = (args, thunkApi) => {
  const { context } = thunkApi.getState().products.ui.selection;

  if (isMasterSelectionContext(context)) {
    return runBulkMasterFieldMutation(args, thunkApi);
  }

  if (isDraftSelectionContext(context)) {
    return runBulkDraftFieldMutation(args, thunkApi);
  }

  return { ignored: true, reason: 'notWritableContext' };
};

export const updateAggregatedField = createAsyncThunk(
  'products/updateAggregatedField',
  async ({
    fieldKey, value, target, isMeta,
  }, thunkApi) => runAggregatedFieldMutation({
    fieldKey,
    value,
    target,
    isMeta,
    isClear: false,
  }, thunkApi),
);

export const clearAggregatedField = createAsyncThunk(
  'products/clearAggregatedField',
  async ({ fieldKey, target, isMeta }, thunkApi) => runAggregatedFieldMutation({
    fieldKey,
    value: undefined,
    target,
    isMeta,
    isClear: true,
  }, thunkApi),
);

const savingKeyFromMeta = (meta) => getAggregatedSavingKey({
  fieldKey: meta.arg?.fieldKey,
  target: meta.arg?.target,
  isMeta: meta.arg?.isMeta,
});

const markSaving = (state, meta) => {
  const savingKey = savingKeyFromMeta(meta);
  state.ui.aggregatedDetails.savingByKey = {
    ...state.ui.aggregatedDetails.savingByKey,
    [savingKey]: meta.requestId,
  };
};

const clearSavingIfLatest = (state, meta) => {
  const savingKey = savingKeyFromMeta(meta);
  if (state.ui.aggregatedDetails.savingByKey?.[savingKey] !== meta.requestId) {
    return false;
  }

  const nextSaving = { ...state.ui.aggregatedDetails.savingByKey };
  delete nextSaving[savingKey];
  state.ui.aggregatedDetails.savingByKey = nextSaving;
  return true;
};

const removeSkippedFromSelection = (state, skipped = []) => {
  if (!skipped.length) return;

  applySelectionCommitToState(state, {
    removeKeys: skipped.map((item) => buildSelectionKey({
      type: item.entityType,
      _id: item.id,
    })),
  });
};

const applyAggregatedFieldFulfilled = (state, { meta, payload }) => {
  const isLatest = clearSavingIfLatest(state, meta);
  if (!isLatest || payload?.ignored) return;

  const savingKey = savingKeyFromMeta(meta);
  state.ui.aggregatedDetails.lastAppliedByKey[savingKey] = meta.requestId;

  removeSkippedFromSelection(state, payload?.summary?.skipped);
  applyBulkMasterFieldPatchToItems(state, payload);
  applyBulkDraftFieldPatchToEntities(state, payload);
};

export const reducer = (builder) => {
  builder
    .addCase(updateAggregatedField.pending, (state, { meta }) => {
      markSaving(state, meta);
    })
    .addCase(clearAggregatedField.pending, (state, { meta }) => {
      markSaving(state, meta);
    })
    .addCase(updateAggregatedField.fulfilled, applyAggregatedFieldFulfilled)
    .addCase(clearAggregatedField.fulfilled, applyAggregatedFieldFulfilled)
    .addCase(updateAggregatedField.rejected, (state, { meta }) => {
      clearSavingIfLatest(state, meta);
    })
    .addCase(clearAggregatedField.rejected, (state, { meta }) => {
      clearSavingIfLatest(state, meta);
    });
};
